import React from "react";
import { Player, GamePhase, PlayerRole } from "../../context/GameContext";
import { useGame } from "../../context/GameContext";

interface PlayersListProps {
  players: Player[];
  currentPlayerId: string;
  phase: GamePhase;
  myRole: PlayerRole;
}

const PlayersList: React.FC<PlayersListProps> = ({
  players,
  currentPlayerId,
  phase,
  myRole,
}) => {
  const { sendMafiaAction, sendDoctorAction, sendDetectiveAction } = useGame();

  const handlePlayerClick = (playerId: string) => {
    // Only allow actions during night phase and if the player is alive
    if (phase !== GamePhase.NIGHT) return;

    // Don't allow actions on self
    if (playerId === currentPlayerId) return;

    // Check if player is still alive
    const targetPlayer = players.find((p) => p.id === playerId);
    if (!targetPlayer?.isAlive) return;

    // Perform action based on role
    switch (myRole) {
      case PlayerRole.MAFIA:
        sendMafiaAction(playerId);
        break;
      case PlayerRole.DOCTOR:
        sendDoctorAction(playerId);
        break;
      case PlayerRole.DETECTIVE:
        sendDetectiveAction(playerId);
        break;
      default:
        // Villagers don't have night actions
        break;
    }
  };

  // Determine if the current user can see who is mafia
  const canSeeMafia = myRole === PlayerRole.MAFIA;

  return (
    <div className="card">
      <h2 className="text-lg font-semibold mb-4">Players</h2>
      <div className="space-y-2">
        {players.map((player) => {
          const isCurrentPlayer = player.id === currentPlayerId;
          const isMafia =
            myRole === PlayerRole.MAFIA && player.role === PlayerRole.MAFIA;

          return (
            <div
              key={player.id}
              onClick={() => handlePlayerClick(player.id)}
              className={`
                p-3 rounded-md flex items-center justify-between
                ${
                  !player.isAlive
                    ? "bg-slate-800 text-slate-500"
                    : phase === GamePhase.NIGHT &&
                      canSeeMafia &&
                      player.role === PlayerRole.MAFIA
                    ? "bg-red-900/30"
                    : "bg-slate-700 hover:bg-slate-600 cursor-pointer"
                }
                ${isCurrentPlayer ? "border-l-4 border-indigo-500" : ""}
                transition-colors
              `}
            >
              <div className="flex items-center">
                <span className={!player.isAlive ? "line-through" : ""}>
                  {player.name}
                  {isCurrentPlayer && " (You)"}
                </span>
                {canSeeMafia &&
                  player.role === PlayerRole.MAFIA &&
                  player.id !== currentPlayerId && (
                    <span className="ml-2 text-xs bg-red-800 px-1.5 py-0.5 rounded-md">
                      Mafia
                    </span>
                  )}
              </div>

              {!player.isAlive && <span className="text-red-500">☠️</span>}
            </div>
          );
        })}
      </div>

      {phase === GamePhase.NIGHT && (
        <div className="mt-4 text-xs text-slate-400">
          {myRole !== PlayerRole.VILLAGER
            ? "Click on a player to perform your night action."
            : "You're sleeping during the night. Special roles are taking actions."}
        </div>
      )}
    </div>
  );
};

export default PlayersList;
